import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { 
  UnifiedCall, 
  ScoringRubric, 
  CallFilters, 
  NotificationSettings,
  DashboardMetrics,
  NotificationRecipient,
  EmailTemplate
} from '../modules/callIntelligence/types';
import { callIntelligenceApi } from '../modules/callIntelligence/api/callIntelligenceApi';

interface CallIntelligenceState {
  calls: {
    items: UnifiedCall[];
    totalCount: number;
    selectedCall: UnifiedCall | null;
    filters: CallFilters;
    loading: boolean;
    error: string | null;
  };
  scoring: {
    rubrics: ScoringRubric[];
    selectedRubric: ScoringRubric | null;
    scoringResults: Record<string, any>;
    loading: boolean;
    error: string | null;
  };
  notifications: {
    settings: NotificationSettings | null;
    recipients: NotificationRecipient[];
    templates: EmailTemplate[];
    loading: boolean;
    error: string | null;
  };
}

const initialState: CallIntelligenceState = {
  calls: {
    items: [],
    totalCount: 0,
    selectedCall: null,
    filters: {},
    loading: false,
    error: null,
  },
  scoring: {
    rubrics: [],
    selectedRubric: null,
    scoringResults: {},
    loading: false,
    error: null,
  },
  notifications: {
    settings: null,
    recipients: [],
    templates: [],
    loading: false,
    error: null,
  },
};

// Async thunks
export const fetchCalls = createAsyncThunk(
  'callIntelligence/fetchCalls',
  async (filters?: CallFilters) => {
    return await callIntelligenceApi.getCalls(filters);
  }
);

export const fetchCallById = createAsyncThunk(
  'callIntelligence/fetchCallById',
  async (id: string) => {
    return await callIntelligenceApi.getCallById(id);
  }
);

export const scoreCall = createAsyncThunk(
  'callIntelligence/scoreCall',
  async ({ id, rubricId }: { id: string; rubricId?: number }) => {
    return await callIntelligenceApi.scoreCall(id, rubricId);
  }
);

export const fetchRubrics = createAsyncThunk(
  'callIntelligence/fetchRubrics',
  async () => {
    return await callIntelligenceApi.getRubrics();
  }
);

export const createRubric = createAsyncThunk(
  'callIntelligence/createRubric',
  async (rubric: Partial<ScoringRubric>) => {
    return await callIntelligenceApi.createRubric(rubric);
  }
);

export const updateRubric = createAsyncThunk(
  'callIntelligence/updateRubric',
  async ({ id, rubric }: { id: number; rubric: Partial<ScoringRubric> }) => {
    return await callIntelligenceApi.updateRubric(id, rubric);
  }
);

export const deleteRubric = createAsyncThunk(
  'callIntelligence/deleteRubric',
  async (id: number) => {
    await callIntelligenceApi.deleteRubric(id);
    return id;
  }
);

export const fetchNotificationSettings = createAsyncThunk(
  'callIntelligence/fetchNotificationSettings',
  async () => {
    return await callIntelligenceApi.getNotificationSettings();
  }
);

export const updateNotificationSettings = createAsyncThunk(
  'callIntelligence/updateNotificationSettings',
  async (settings: NotificationSettings) => {
    return await callIntelligenceApi.updateNotificationSettings(settings);
  }
);

export const fetchRecipients = createAsyncThunk(
  'callIntelligence/fetchRecipients',
  async () => {
    return await callIntelligenceApi.getRecipients();
  }
);

export const addRecipient = createAsyncThunk(
  'callIntelligence/addRecipient',
  async (recipient: Partial<NotificationRecipient>) => {
    return await callIntelligenceApi.addRecipient(recipient);
  }
);

export const updateRecipient = createAsyncThunk(
  'callIntelligence/updateRecipient',
  async ({ id, recipient }: { id: number; recipient: Partial<NotificationRecipient> }) => {
    return await callIntelligenceApi.updateRecipient(id, recipient);
  }
);

export const deleteRecipient = createAsyncThunk(
  'callIntelligence/deleteRecipient',
  async (id: number) => {
    await callIntelligenceApi.deleteRecipient(id);
    return id;
  }
);

export const fetchEmailTemplates = createAsyncThunk(
  'callIntelligence/fetchEmailTemplates',
  async () => {
    return await callIntelligenceApi.getEmailTemplates();
  }
);

export const updateEmailTemplate = createAsyncThunk(
  'callIntelligence/updateEmailTemplate',
  async ({ id, template }: { id: number; template: Partial<EmailTemplate> }) => {
    return await callIntelligenceApi.updateEmailTemplate(id, template);
  }
);

export const fetchDashboardMetrics = createAsyncThunk(
  'callIntelligence/fetchDashboardMetrics',
  async () => {
    return await callIntelligenceApi.getDashboardMetrics();
  }
);


const callIntelligenceSlice = createSlice({
  name: 'callIntelligence',
  initialState,
  reducers: {
    setSelectedCall: (state, action: PayloadAction<UnifiedCall | null>) => {
      state.calls.selectedCall = action.payload;
    },
    setCallFilters: (state, action: PayloadAction<CallFilters>) => {
      state.calls.filters = action.payload;
    },
    setSelectedRubric: (state, action: PayloadAction<ScoringRubric | null>) => {
      state.scoring.selectedRubric = action.payload;
    },
    clearErrors: (state) => {
      state.calls.error = null;
      state.scoring.error = null;
      state.notifications.error = null;
    },
  },
  extraReducers: (builder) => {
    // Calls
    builder
      .addCase(fetchCalls.pending, (state) => {
        state.calls.loading = true;
        state.calls.error = null;
      })
      .addCase(fetchCalls.fulfilled, (state, action) => {
        state.calls.loading = false;
        state.calls.items = action.payload.data;
        state.calls.totalCount = action.payload.meta.totalCount;
      })
      .addCase(fetchCalls.rejected, (state, action) => {
        state.calls.loading = false;
        state.calls.error = action.error.message || 'Failed to fetch calls';
      })
      .addCase(fetchCallById.pending, (state) => {
        state.calls.loading = true;
        state.calls.error = null;
      })
      .addCase(fetchCallById.fulfilled, (state, action) => {
        state.calls.loading = false;
        state.calls.selectedCall = action.payload;
      })
      .addCase(fetchCallById.rejected, (state, action) => {
        state.calls.loading = false;
        state.calls.error = action.error.message || 'Failed to fetch call details';
      })
      .addCase(scoreCall.fulfilled, (state, action) => {
        const index = state.calls.items.findIndex(c => c.id === action.payload.id);
        if (index !== -1) {
          state.calls.items[index] = action.payload;
        }
        if (state.calls.selectedCall?.id === action.payload.id) {
          state.calls.selectedCall = action.payload;
        }
      });

    // Rubrics
    builder
      .addCase(fetchRubrics.pending, (state) => {
        state.scoring.loading = true;
        state.scoring.error = null;
      })
      .addCase(fetchRubrics.fulfilled, (state, action) => {
        state.scoring.loading = false;
        state.scoring.rubrics = action.payload;
      })
      .addCase(fetchRubrics.rejected, (state, action) => {
        state.scoring.loading = false;
        state.scoring.error = action.error.message || 'Failed to fetch rubrics';
      })
      .addCase(createRubric.fulfilled, (state, action) => {
        state.scoring.rubrics.push(action.payload);
      })
      .addCase(updateRubric.fulfilled, (state, action) => {
        const index = state.scoring.rubrics.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.scoring.rubrics[index] = action.payload;
        }
      })
      .addCase(deleteRubric.fulfilled, (state, action) => {
        state.scoring.rubrics = state.scoring.rubrics.filter(r => r.id !== action.payload);
      });

    // Notifications
    builder
      .addCase(fetchNotificationSettings.pending, (state) => {
        state.notifications.loading = true;
        state.notifications.error = null;
      })
      .addCase(fetchNotificationSettings.fulfilled, (state, action) => {
        state.notifications.loading = false;
        state.notifications.settings = action.payload;
      })
      .addCase(fetchNotificationSettings.rejected, (state, action) => {
        state.notifications.loading = false;
        state.notifications.error = action.error.message || 'Failed to fetch notification settings';
      })
      .addCase(updateNotificationSettings.fulfilled, (state, action) => {
        state.notifications.settings = action.payload;
      })
      .addCase(fetchRecipients.fulfilled, (state, action) => {
        state.notifications.recipients = action.payload;
      })
      .addCase(addRecipient.fulfilled, (state, action) => {
        state.notifications.recipients.push(action.payload);
      })
      .addCase(updateRecipient.fulfilled, (state, action) => {
        const index = state.notifications.recipients.findIndex(r => r.id === action.payload.id);
        if (index !== -1) {
          state.notifications.recipients[index] = action.payload;
        }
      })
      .addCase(deleteRecipient.fulfilled, (state, action) => {
        state.notifications.recipients = state.notifications.recipients.filter(r => r.id !== action.payload);
      })
      .addCase(fetchEmailTemplates.fulfilled, (state, action) => {
        state.notifications.templates = action.payload;
      })
      .addCase(updateEmailTemplate.fulfilled, (state, action) => {
        const index = state.notifications.templates.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.notifications.templates[index] = action.payload;
        }
      });

    // Dashboard
    builder
      .addCase(fetchDashboardMetrics.pending, (state) => {
        // Dashboard metrics can be added to a dashboard state if needed
      })
      .addCase(fetchDashboardMetrics.fulfilled, (state, action) => {
        // Dashboard metrics handling
      })
      .addCase(fetchDashboardMetrics.rejected, (state, action) => {
        // Dashboard error handling
      });
  },
});

export const {
  setSelectedCall,
  setCallFilters,
  setSelectedRubric,
  clearErrors,
} = callIntelligenceSlice.actions;

export default callIntelligenceSlice.reducer;